import { Box, ButtonBase, Drawer, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';

/**
 * Bottom sheet with large, thumb-friendly rows. Replaces small anchored
 * menus and popovers on touch devices.
 *
 * actions: [{ key, label, Icon, onClick, color, disabled }]
 * Tapping an action closes the sheet first, then runs it.
 * `children` is shown below the actions (e.g. a folder tree).
 */
export const ECActionSheet = ({ open, onClose, title, actions = [], children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Drawer
      anchor="bottom"
      open={Boolean(open)}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        '& > .MuiPaper-root': {
          bgcolor: colors.glassSolid,
          backgroundImage: 'none',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          maxHeight: '85%',
          paddingBottom: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      <Box className="w-full flex justify-center pt-2 pb-1">
        <Box
          className="h-[5px] w-[48px] rounded-full"
          sx={{ bgcolor: colors.selected }}
        />
      </Box>
      {title && (
        <Typography fontWeight={600} className="px-4 pt-1 pb-2 opacity-70">
          {title}
        </Typography>
      )}
      <Box className="flex flex-col pb-2 overflow-y-auto" sx={{ overscrollBehavior: 'contain' }}>
        {actions
          .filter(Boolean)
          .map(({ key, label, Icon, onClick, color, disabled }) => (
            <ButtonBase
              key={key || label}
              disabled={disabled}
              onClick={() => {
                onClose?.();
                onClick?.();
              }}
              className="flex items-center justify-start gap-4 px-4 w-full"
              sx={{
                minHeight: '52px',
                color: color || colors.text,
                opacity: disabled ? 0.4 : 1,
                textAlign: 'left',
              }}
            >
              {Icon && <Icon sx={{ fontSize: '24px', color: color || 'inherit' }} />}
              <Typography fontSize={16} sx={{ color: 'inherit' }}>
                {label}
              </Typography>
            </ButtonBase>
          ))}
        {children}
      </Box>
    </Drawer>
  );
};
